import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  finalize,
  map,
  Observable,
  shareReplay,
  takeUntil,
  tap,
} from 'rxjs';
import { PageNames } from 'src/app/configs/page-names.enum';
import {
  CustomField,
  CustomResult,
  CustomResultOfPaginatedListOfPhonebookDto,
  CustomResultOfPaginatedListOfPhonebooksByPrivateNumberDto,
  GetEnumValuesDto,
  PhonebookDto,
  PhoneBookRecipientRequest,
  PhonebookTypes,
} from 'src/app/core/api/models';
import {
  PhonebookCustomFieldsService,
  PhonebooksService,
  WhiteMessagesService,
} from 'src/app/core/api/services';
import { cloneDeepObj } from 'src/app/core/utils/util';
import { UseSeparatorService } from 'src/app/layout/components/explore-main-drawer/use-separator.service';
import { BaseIndexModule } from 'src/app/pages/shared/base';
import { PageTemplateModule } from 'src/app/shared/global/page-template/page-template.module';
import { BaseTreeTablePageComponent } from '../../../shared/base/components/base-tree-table-page.component';
import { PhonebookFormComponent } from '../phonebook-form/phonebook-form.component';
import { PhonebookRowMenuOperation } from '../phonebook-row/phonebook-row-menu/phonebook-row-menu.component';
import { PhonebookCustomDto } from '../shared/phonebook-custom.model';
import { PhonebookStateService } from '../shared/phonebook-state.service';
import { EventTypes } from './../../../../core/constants/event-types';
import { AppDomService } from './../../../../core/services/dom.service';
import { FormFieldSearcherModule } from './../../../../shared/components/form-fields/form-field-searcher/form-field-searcher.module';
import { DynamicTableModule } from './../../../../shared/global/dynamic-table/dynamic-table.module';
import { FormBtnsModule } from './../../../../shared/global/form-btns/form-btns.module';
import { TdValueModule } from './../../../../shared/global/td-value/td-value.module';
import { ContactsComponent } from './../../contacts/contacts.component';
import { PhonebookRowComponent } from './../phonebook-row/phonebook-row.component';

@Component({
  selector: 'phonebook-list',
  imports: [
    FormFieldSearcherModule,
    ReactiveFormsModule,
    FormsModule,
    FormBtnsModule,
    DynamicTableModule,
    TdValueModule,
    PhonebookRowComponent,
    PageTemplateModule,
    MatTooltipModule,
    MatCheckboxModule,
    DynamicTableModule,
    BaseIndexModule,
  ],
  templateUrl: './phonebook-list.component.html',
})
export class PhonebookListComponent extends BaseTreeTablePageComponent {
  //#region props
  pageName: string = PageNames.phonebook;
  protected currentService = inject(PhonebooksService);
  deleteApiMethodName: string = this.currentService.phonebooksDelete.name;
  createGetApiMethodName: string = this.currentService.phonebooksCreateGet.name;
  pageId: string = 'phonebook';
  useSeparatorService = inject(UseSeparatorService);
  phonebookStateService = inject(PhonebookStateService);
  modelWithPagination$: Observable<CustomResultOfPaginatedListOfPhonebookDto>;
  items$: Observable<Array<PhonebookDto>>;
  items: PhonebookCustomDto[] = [];
  phonebookId: number;
  selectedPhonebook: PhonebookDto = { id: 0 };
  isShowDynamicTemplate: boolean = false;
  isUpdatePhonebookCustomFields: boolean = false;
  phonebookCustomFields: CustomField[] = [];
  selectedPhonebooks: PhonebookDto[] = [];
  PhonebookTypesEnumValues: GetEnumValuesDto[] = [];
  @Input() contactsComponent: ContactsComponent;
  @Input() phonebookRecipients: PhonebookCustomDto[] = [];
  @Input() isShowInputs: boolean = true;
  @Input() isShowFullTable: boolean = false;
  @Input() isShowParentBtn: boolean = true;
  @Input() isFixedHeight: boolean = false;
  @Input() isWhiteMessagePhonebook: boolean;
  @Input() privateNumber: any;
  @Input() selectedPhonebookIds: number[] = [];
  @Input() justCurrentOrganization: boolean = true;
  @Input() isShowHeader: boolean = false;
  @Output() phonebookRecipientsChange = new EventEmitter<
    PhoneBookRecipientRequest[]
  >();
  @Output() selectedPhonebooksChange = new EventEmitter<number[]>();
  @Output() onShowPhonebookHelp = new EventEmitter<boolean>();
  @Output() onShowPhonebookAudit = new EventEmitter<boolean>();
  @Output() onShowCustomFields = new EventEmitter<boolean>();
  @Output() selectedPhonebookChange = new EventEmitter<PhonebookDto>();
  @Output() showAdvanceSearch = new EventEmitter<boolean>();
  @Output() justCurrentOrganizationChange = new EventEmitter<boolean>();
  @Output() callRefresh = new EventEmitter<any>();
  @Output() customFields = new EventEmitter<Array<CustomField>>();

  get PhonebookTypes() {
    return PhonebookTypes;
  }
  isShowAdvanceSearch: boolean = false;
  isUseSeparator: boolean = true;
  isSelectedAll: boolean;
  //#endregion

  constructor(
    private whiteMessagesService: WhiteMessagesService,
    private phonebookCustomFieldsService: PhonebookCustomFieldsService,

    protected translate: TranslateService
  ) {
    super();
    effect(() => {
      this.isUseSeparator = this.useSeparatorService.useSeparator();
    });
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.defaultSelectTreeItem();
      this.getEnumsValues();
      this.pageConfig.isShowTable = false;
      this.pageConfig.showHeader = this.isShowHeader;
      if (!this.isShowFullTable) {
        this.pageConfig.tableConfig.showOperationColumn = false;
        this.emit();
      }
      if (this.isFixedHeight) this.pageConfig.tableConfig.isFixedHeight = true;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  getEnumsValues() {
    this.getEnumValuesApi('PhonebookTypes')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.PhonebookTypesEnumValues = values));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['privateNumber']) {
      this.phonebookRecipients = [];
      this.selectedPhonebookIds = [];
      this.selectedPhonebooks = [];
      this.emitRecipients();
      this.emitPhonebookIds();
      this.getTableData();
    }
  }

  changePhonebookRecipients() {
    if (!this.phonebookRecipients?.length) {
      this.phonebookStateService.deepChangePhonebooksState(
        this.items,
        'checked',
        false
      );
      this.selectedPhonebookIds = [];
    } else {
      this.phonebookStateService.applySelectedPhonebooks(
        this.items,
        this.phonebookRecipients
      );
      this.selectedPhonebookIds = this.phonebookRecipients.map((p) => p.id);
    }
  }

  //#region init

  configTableColumns() {
    try {
      this.tableColumns = [
        {
          title: ``,
          name: '',
          hasSearch: false,
          noSorting: true,
          isCheckBox: true,
          wrap: true,
        },
        {
          title: this.localizer.name,
          name: 'name',
          hasSearch: true,
          colSpan: 3,
        },
        {
          title: 'common.type',
          name: 'phonebookTypes',
          hasSearch: true,
          type: 'enum',
          noSorting: true,
          enumName: 'PhonebookTypes',
        },
        { title: 'common.contactsCount', name: 'contactsCount' },
      ];
      if (this.isShowFullTable)
        this.tableColumns = this.tableColumns.concat([
          {
            title: `common.whiteMessageId`,
            name: 'whiteMessageId',
            hasSearch: false,
          },
          {
            title: this.localizer.createDate,
            name: 'created',
            hasSearch: false,
            type: 'date',
          },
        ]);
      else if (this.isShowInputs)
        this.tableColumns = this.tableColumns.concat([
          {
            title: 'common.fromIndex',
            name: 'fromIndex',
            hasSearch: false,
            noSorting: true,
          },
          {
            title: 'common.count',
            name: 'count',
            hasSearch: false,
            noSorting: true,
          },
        ]);
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smsReport);
    }
  }

  getTableData() {
    try {
      this.callRefresh.emit(true);

      if (this.isWhiteMessagePhonebook && !this.privateNumber) {
        if (this.pageConfig?.tableConfig) {
          this.pageConfig.tableConfig.paginationModel.totalCount = 0;
          this.items = null;
        }
        return;
      }

      this.pageConfig.loading = true;
      this.pageConfig.hasError = false;
      this.unCheckSelectAll();

      //if private number is selected in white message
      if (this.isWhiteMessagePhonebook)
        this.modelWithPagination$ =
          this.getPhonebooksWithPaginationByPrivateNumberApi(
            this.privateNumber
          ).pipe(
            shareReplay(),
            catchError((error) => {
              this.pageConfig.isEmpty = false;
              this.pageConfig.hasError = true;
              return error;
            })
          );

      //#endregion

      if (!this.isWhiteMessagePhonebook)
        this.modelWithPagination$ = this.getPaginationApi().pipe(
          shareReplay(),
          catchError((error) => {
            this.pageConfig.isEmpty = false;
            this.pageConfig.hasError = true;
            return error;
          })
        );

      this.getItems();
      this.setTreeItems(this.items$);
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  setTreeItems(items$: Observable<Array<PhonebookDto>>) {
    try {
      items$
        .pipe(
          takeUntil(this.onDestroy$),
          map((items) => {
            return items.map((item) => {
              return { subItems: [], ...item };
            });
          }),
          tap((items) => {
            this.selectedPhonebook = { ...items[0] };
            this.emitSelectedPhonebook();
          }),
          finalize(() => {
            if (this.items?.length)
              this.phonebookStateService.applySelectedPhonebooks(
                this.items,
                this.phonebookRecipients
              );
            if (this.isSelectedAll)
              this.phonebookStateService.deepChangePhonebooksState(
                this.items,
                'checked',
                true
              );
          })
        )
        .subscribe((items) => {
          this.items = items;
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  //#endregion

  //#region Api

  //Get All
  getPaginationApi(): Observable<CustomResultOfPaginatedListOfPhonebookDto> {
    return this.currentService.phonebooksGetPhonebooksWithPagination({
      body: {
        ...this.getBaseModelWithPaginationParams(),
        justCurrentOrganization: this.justCurrentOrganization,
      },
    });
  }

  getPhonebooksWithPaginationByPrivateNumberApi(
    privateNumberId
  ): Observable<CustomResultOfPaginatedListOfPhonebooksByPrivateNumberDto> {
    return this.whiteMessagesService.whiteMessagesGetPhonebooksByPrivateNumber({
      body: {
        pageNumber:
          this.paginationModel.pageSize > this.paginationModel.totalCount
            ? 1
            : this.paginationModel.pageNumber,
        pageSize: this.paginationModel.pageSize,
        orders: this.sortObject,
        comparisonObjects: this.comparisonObjects,
        privateNumberId: privateNumberId,
      },
    });
  }

  //Get ById
  baseGetByIdApi(id: number): void {
    this.unSubs();

    this.pageConfig.loading = true;

    this.formPageConfig.editId = id;
    this.currentService
      .phonebooksUpdateGet({ id: id })
      .pipe(this.basePipeOperators(), shareReplay())
      .subscribe(this.baseGetByIdObserverFormModal);
  }

  //Get CustomFields By PhonebookId
  createCustomFieldByPhonebookGetApi(phonebookId: number) {
    this.phonebookCustomFieldsService
      .phonebookCustomFieldsUpdateCustomFieldByPhonebookGet()
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) {
          this.getPhonebookCustomFieldsApi(phonebookId);
        }
      });
  }

  getPhonebookCustomFieldsApi(phonebookId: number) {
    this.unSubs();
    this.pageConfig.loading = true;
    this.phonebookCustomFieldsService
      .phonebookCustomFieldsGetCustomFieldByPhonebook({
        phonebookId: phonebookId,
      })
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) {
          this.phonebookCustomFields = res.data;
          this.customFields.emit(this.phonebookCustomFields);
          this.onShowCustomFields.emit(true);
          this.phonebookId = phonebookId;
        }
      });
  }

  deleteManyPhonebookByIdListApi(ids: number[]) {
    this.unSubs();

    this.pageConfig.loading = true;

    this.currentService
      .phonebooksDeleteManyByIdList({ body: { ids: ids } })
      .pipe(
        this.basePipeOperators(),
        finalize(() => (this.selectedPhonebooks = []))
      )
      .subscribe((result: CustomResult) => {
        this.apiNotificationService.notifyMessage({ result });
        if (result.isSucceed) {
          this.selectedPhonebooks = [];
          this.phonebookRecipients = [];
          this.getTableData();
        }
      });
  }

  //#endregion

  //#region phonebook Model
  emit() {
    this.emitRecipients();
    this.emitPhonebookIds();
  }

  emitRecipients() {
    const phonebookRecipients = cloneDeepObj(this.phonebookRecipients);
    this.selectedPhonebooks = phonebookRecipients || [];
    this.phonebookRecipientsChange.emit(phonebookRecipients || []);
  }

  emitPhonebookIds() {
    this.selectedPhonebookIds = [];
    if (this.phonebookRecipients)
      this.selectedPhonebookIds = cloneDeepObj(
        this.phonebookRecipients?.map((p) => p?.id)
      );

    this.selectedPhonebooksChange.emit(this.selectedPhonebookIds || []);
  }

  onChangeState(phonebook: PhonebookCustomDto) {
    this.phonebookStateService.updateSelectedRecipients(
      [phonebook],
      this.phonebookRecipients
    );
    this.emit();
  }

  updatePhonebookContactCount(event) {
    this.phonebookStateService.updatePhonebook(
      this.items,
      this.selectedPhonebook.id,
      'contactsCount',
      event
    );
  }

  //#endregion

  //#region menu operations

  onGroupDeletion() {
    if (this.selectedPhonebooks?.length === 0) return;
    this.groupDeletionConfirmation();
  }

  onAdvanceSearch() {
    this.isShowAdvanceSearch = !this.isShowAdvanceSearch;
    this.showAdvanceSearch.emit(this.isShowAdvanceSearch);
    // const modalResult = this.dialogService
    //   .openModal({
    //     component: PhonebookAdvanceSearchDialogComponent,
    //   })
    //   .afterClosed()
    //   .pipe(takeUntil(this.onDestroy$))
    //   .subscribe((result) => {});
  }

  groupDeletionConfirmation() {
    try {
      let ids: number[] = this.selectedPhonebooks.map((item) => item.id);
      let names: string[] = this.selectedPhonebooks.map((item) => item.name);

      const dialogResult = this.getConfirmDialog({
        title: 'messages.groupDeletionConfirmation',
        // content: names.join().replace(/,/g, ' - '),
        isConfirmDelete: true,
        wordBreak: false,
        confirmByWritingText: true,
        isShowChip: true,
      });

      dialogResult
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result) {
            this.deleteManyPhonebookByIdListApi(ids);
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  openCreateFormModal() {
    this.unSubs();
    let data = {
      isEdit: false,
      title: `${this.formPageConfig?.vocabs}addTitle`,
      parentId:
        this.selectedPhonebook.parentId != null
          ? this.selectedPhonebook?.id
          : null,
    };

    const modalResult = this.dialogService.openModal({
      component: PhonebookFormComponent,
      data: data,
    });
    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.pageConfig.isVisibleForm = undefined)),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.getTableData();
          this.cd.detectChanges();
        }
      });
  }

  openEditFormModal(model: PhonebookDto): void {
    this.formPageConfig.editId = model.id;

    let data = {
      title: `${this.formPageConfig?.vocabs}editTitle`,
      isEdit: true,
      model: model,
      editData: model,
    };

    const modalResult = this.dialogService.openModal({
      component: PhonebookFormComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.getTableData();
          this.cd.detectChanges();
        }
      });
  }

  onDelete(model) {
    let dialogResult = this.dialogService.confirmDialog({
      content: model.name,
      isConfirmDelete: true,
      confirmByWritingText: true,
    });
    dialogResult.afterClosed().subscribe((result) => {
      if (result) {
        this.baseDeleteApi(model.id);
      }
    });
  }

  showCustomFields(phonebookId: number) {
    this.createCustomFieldByPhonebookGetApi(phonebookId);
  }

  onShowDynamicTemplate(phonebookId: number) {
    this.phonebookId = phonebookId;
    this.isShowDynamicTemplate = true;
  }

  onClickShow() {
    this.contactsComponent.showForm('create');
  }

  onAddSingle(phoneBook: PhonebookCustomDto) {
    try {
      this.contactsComponent.phonebookId = phoneBook.id;
      this.contactsComponent.selectedPhonebook = phoneBook;
      this.contactsComponent.showForm('create');
      this.onSelect(phoneBook);
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  onAddListNumber(phoneBook: PhonebookCustomDto) {
    try {
      this.contactsComponent.phonebookId = phoneBook.id;
      this.contactsComponent.selectedPhonebook = phoneBook;
      this.contactsComponent.onShowContactsNumberList();
      this.contactsComponent.emitIsShowContactsForm();
      this.onSelect(phoneBook);
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  onAddFromFile(phoneBook: PhonebookCustomDto) {
    try {
      this.contactsComponent.phonebookId = phoneBook.id;
      this.contactsComponent.selectedPhonebook = phoneBook;
      this.contactsComponent.onShowImportContactsFromXlsx();
      this.contactsComponent.emitIsShowContactsForm();
      this.onSelect(phoneBook);
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  onDeleteAllContact(phoneBook: PhonebookCustomDto) {
    this.contactsComponent.deleteAllContactsConfirmation(phoneBook.id);
  }

  onDownload(phoneBook: PhonebookCustomDto) {
    try {
      this.contactsComponent.phonebookId = phoneBook.id;
      this.contactsComponent.downloadContactsGetApi();
      this.onSelect(phoneBook);
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.facility);
    }
  }

  //#endregion

  onSelect(event: PhonebookCustomDto) {
    this.selectedPhonebook = event;
    this.emitSelectedPhonebook();
  }

  onUpdate(id?, setUrl = true) {
    if (
      window.location.pathname != '/occasionScheduledMessage' &&
      window.location.pathname != '/sendQueue' &&
      window.location.pathname != '/usersSendQueue' &&
      window.location.pathname != '/currentUserSendQueue' &&
      window.location.pathname != '/resellerSendQueue'
    )
      this.baseGetByIdApi(id);
  }

  onOperation(operation: string, item: PhonebookCustomDto) {
    switch (operation as PhonebookRowMenuOperation) {
      case PhonebookRowMenuOperation.addSingle:
        this.onAddSingle(item);
        break;
      case PhonebookRowMenuOperation.addFromFile:
        this.onAddFromFile(item);
        break;
      case PhonebookRowMenuOperation.addListNumber:
        this.onAddListNumber(item);
        break;
      case PhonebookRowMenuOperation.edit:
        this.onUpdate(item.id);
        break;
      case PhonebookRowMenuOperation.customFields:
        this.showCustomFields(item.id);
        break;
      case PhonebookRowMenuOperation.downloadAll:
        this.onDownload(item);
        break;
      case PhonebookRowMenuOperation.delete:
        this.onDelete(item);
        break;
      case PhonebookRowMenuOperation.deleteAll:
        this.onDeleteAllContact(item);
        break;
    }
  }

  onSelectAll(checked) {
    this.isSelectedAll = checked;
    this.phonebookStateService.selectAll(this.items, checked);
    this.emit();
  }

  emitSelectedPhonebook() {
    if (!this.isShowAdvanceSearch)
      this.selectedPhonebookChange.emit(this.selectedPhonebook);
  }
}
